<template>
	<layout-page max-screen-height="true" title="Search">
		<search-filter
			v-model="filter"
			:key="'FILTER_SLIDEOVER_' + showFilter"
			ref="searchFilter"
			v-if="showFilter"
			v-on:apply="applyFilter()"
			v-on:close="applyFilter()"
			v-on:clear-filter="clearFilter"
			:mapboxToken="mapboxToken"
			:user="user"
		/>

		<div class="max-h-full overflow-hidden flex-grow flex flex-col">
			<!-- header -->
			<div
				class="bg-green px-4 sm:px-6 py-4 md:rounded-tl-2xl xl:rounded-t-2xl flex justify-start"
			>
				<h1 class="text-2xl md:text-4xl font-semibold text-white">
					{{ __("What are you looking for?") }}
				</h1>
			</div>

			<!-- filter bar -->
			<div
				class="px-4 sm:px-6 lg:px-6 py-4 bg-gray-700 flex flex-wrap justify-between items-center gap-x-2 sm:gap-x-6 gap-y-4"
			>
				<!-- filter button -->
				<button
					class="relative inline-flex items-center px-2 lg:pr-4 py-1.5 font-medium text-white bg-gray-500 hover:bg-gray-400 rounded-lg flex items-center focus:ring-2 ring-gray-400"
					v-on:click="showFilter = !showFilter"
				>
					<ui-icon
						class="mr-2 flex-shrink-0 h-5 w-5"
						name="filter"
						stroke-width="2"
					/>
					{{ __("Filter") }}
					<span
						class="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-orange-50 transform translate-x-1/4 -translate-y-1/2 bg-orange rounded-full"
						v-if="activeFilterCount > 0"
						>{{ activeFilterCount }}</span
					>
				</button>

				<!-- offers buy and sell checkboxes -->
				<div class="flex items-center space-x-4 text-gray-100">
					<div class="flex items-center">
						<input
							id="angebote"
							name="angebote"
							type="checkbox"
							v-model="filter.trade.BUY"
							checked="checked"
							@change="applyFilter()"
							class="h-5 w-5 bg-gray-600 border-gray-600 rounded text-orange focus:ring-orange hover:ring-4 hover:ring-orange-800 cursor-pointer"
						/>
						<label
							for="angebote"
							class="pl-3 block font-medium whitespace-nowrap cursor-pointer"
							>{{ __("Buy Offers") }}</label
						>
					</div>
					<div class="flex items-center">
						<input
							id="nachfrage"
							name="nachfrage"
							type="checkbox"
							v-model="filter.trade.SELL"
							checked="checked"
							@change="applyFilter()"
							class="h-5 w-5 bg-gray-600 border-gray-600 rounded text-orange focus:ring-orange border-gray-200 rounded hover:ring-4 hover:ring-orange-800 cursor-pointer"
						/>
						<label
							for="nachfrage"
							class="pl-3 block font-medium whitespace-nowrap cursor-pointer"
							>{{ __("Sell Offers") }}</label
						>
					</div>
				</div>

				<!-- sort by: not available on mobile -->
				<ui-dropdown
					class=""
					button-class="flex w-full items-center justify-between whitespace-nowrap truncate relative px-2 sm:pl-4 sm:pr-2 py-1.5
                                    font-medium text-gray-100 bg-gray-500 hover:bg-gray-400 rounded-lg
                                    active:bg-gray-400 focus:gray-400 focus:ring-2 ring-gray-500 sm:min-w-[20rem]"
					dropdownClass="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10"
				>
					<template v-slot:button>
						<span class="hidden sm:inline">
							{{ __("Sort by") }}
							{{ __(currentSortOrder.label) }}
						</span>
						<!-- TO DO: show applied filter here -->
						<ui-icon
							class="hidden sm:inline-block sm:ml-2 sm:-mr-1 flex-shrink-0 h-5 w-5"
							name="chevron-down"
							stroke-width="2"
						/>
						<ui-icon
							class="sm:hidden flex-shrink-0 h-5 w-5"
							name="arrow-up"
							stroke-width="2"
						/>
						<ui-icon
							class="sm:hidden -ml-1 flex-shrink-0 h-5 w-5"
							name="arrow-down"
							stroke-width="2"
						/>
					</template>

					<!-- TO DO: add 'Show all' option to show all messages (disable filter) -->
					<template v-slot:items class="z-20 right-0">
						<button
							v-for="option in sortOrderOptions"
							:key="'sort_option_' + option.id"
							class="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:bg-orange-500 focus:text-white"
							type="button"
							@click="sortOrder = option.id"
						>
							{{ __(option.label) }}
						</button>
					</template>
				</ui-dropdown>

				<!-- MAP
                <button
                    class="md:hidden flex-shrink pl-2 pr-2 xl:pr-4 py-1.5 font-medium text-white bg-gray-600 hover:bg-gray-400 rounded-lg flex items-center focus:ring-2 ring-gray-500"
                    v-on:click.stop="showListOnMobile = !showListOnMobile"
                >
                    <ui-icon
                        class="2xl:mr-4 flex-shrink-0 h-5 w-5"
                        :name="showListOnMobile ? 'map' : 'list'"
                        stroke-width="2"
                    />
                </button>-->
			</div>

			<!-- map and results -->

			<div class="overflow-hidden bg-gray-100 flex-grow flex">
				<!-- Extra DIV required for Safari; Safari seems to go crazy when grid element has also flex-grow -->
				<div
					class="w-full grid grid-rows-1 grid-cols-1 md:grid-cols-16 2xl:grid-cols-5"
				>
					<!-- map -->
					<crop-map
						:class="[
							'md:col-span-9 2xl:col-span-3',
							showListOnMobile ? 'hidden md:block' : '',
						]"
					>
						<search-map-marker
							v-for="(result, idx) in sortedResults"
							:key="'MARKER_' + idx + '_' + result.match_id"
							:active-id="activeId"
							:item="result"
							:user="user"
						/>
						<search-map-center :search="search" />
						<template v-slot:other>
							<search-map-circle
								:search="search"
								v-if="search.location.distance_max"
							/>
						</template>
					</crop-map>

					<!-- result list -->
					<div
						v-if="sortedResults.length > 0"
						:class="[
							'md:col-span-7 2xl:col-span-2 space-y-4 overflow-y-auto py-4 pl-4 pr-2 md:pr-4 ',
							showListOnMobile ? '' : 'hidden md:block',
						]"
					>
						<!-- legend
                        <div class="flex gap-x-6 ml-auto" v-if="initialFilter.trade.BUY && initialFilter.trade.SELL">
                            <div class="whitespace-nowrap">
                                <div class="inline-block align-text-bottom bg-orange-500 w-5 h-5 mr-1"></div>
                                {{ __('Buy Offers') }}
                            </div>
                            <div class="whitespace-nowrap">
                                <div class="inline-block align-text-bottom bg-green-500 rounded-full w-5 h-5 mr-1"></div>
                                {{ __('Sell Offers') }}
                            </div>
                        </div>-->
						<ul class="space-y-4">
							<li
								v-for="(result, idx) in sortedResults"
								v-bind:class="{ active: activeId == result.match_id }"
								v-on:click="setActive(result.match_id)"
								:key="'ITEM_' + idx + '_' + result.match_id"
								:ref="activeId == result.match_id ? 'activeLi' : null"
							>
								<search-result
									:result="result"
									:is-active="activeId == result.match_id"
									:key="'SEARCH_RESULT_' + result.match_id"
									:user="user"
								/>
							</li>
						</ul>
					</div>
					<content-search-no-matching-results v-else />
				</div>
			</div>
		</div>
	</layout-page>
</template>

<script>
import Vue from "vue";
import UiFilterSessionStorage from "../components/Ui/FilterSessionStorage.vue";

export default {
	mixins: [UiFilterSessionStorage],
	props: {
		results: {
			type: Array,
			required: true,
		},
		search: {
			type: Object,
			required: true,
		},
		initialFilter: {
			type: Object,
			required: true,
		},
		emptyFilter: {
			type: Object,
			required: true,
		},
		mapboxToken: {
			type: String,
			required: true,
		},
		user: {
			type: Object,
			required: false,
		},
	},
	data() {
		return {
			filter: Vue.observable(JSON.parse(JSON.stringify(this.initialFilter))),
			resultsCopy: [...this.results],
			viewType: "list",
			showFilter: false,
			showListOnMobile: true,
			view: "list",
			activeId: null,
			sortOrder: "relevance",
			sortOrderOptions: [
				{
					id: "relevance",
					label: "Relevance",
				},
				{
					id: "distance_km",
					label: "Shortest distance",
					asc: true,
				},
				{
					id: "updated_at",
					label: "Newest listing",
					asc: false,
				},
				{
					id: "price_sort",
					label: "Lowest price",
					asc: true,
				},
				{
					id: "price_estimate_HIGH",
					label: "Highest price",
					field: "price_sort",
					asc: false,
				},
			],
		};
	},
	methods: {
		setActive(id) {
			console.log("setting active", id);
			this.activeId = id;
		},
		scrollToActiveListItem: function () {
			let activeEl = (this.$refs.activeLi || [])[0];
			console.log("SCROLL", activeEl);
			if (activeEl) {
				activeEl.scrollIntoView({ behavior: "smooth", block: "center" });
				activeEl.scroll(0, -100);
			}
		},
		applyFilter() {
			let that = this;
			this.$inertia.post(this.$route("search.index"), this.filter, {
				onFinish() {
					console.log("DONE FILTERING");
					that.showFilter = false;
				},
			});
			console.log("FILTERING");
		},
		clearFilter() {
			this.filter = Object.assign(
				{},
				JSON.parse(JSON.stringify(this.emptyFilter))
			);
			console.log("CLEARING", this.initialFilter);
			this.$refs.searchFilter.filter = this.filter;
		},
	},
	computed: {
		activeFilterCount() {
			let count = 0;
			try {
				count =
					this.filter.product.grade.length +
					this.filter.product.harvest.length +
					this.filter.location.mode.length +
					this.filter.date.type.length;
			} catch (e) {}
			return count;
		},
		currentSortOrder() {
			let sortOrder = this.sortOrder;
			return this.sortOrderOptions.filter((o) => o.id == sortOrder)[0];
		},
		sortedResults() {
			let so = this.currentSortOrder;
			return _.orderBy(
				this.resultsCopy.map(function (r) {
					// for sorting: sort by price.mt of the offer
					r.price_sort = 1 * r.offer.price.mt;

					// when sorting price, always show NULL values last
					if (r.offer.price.mt == null) {
						r.price_sort = (6100 * 10) ^ (5 * (so.asc ? 1 : -1));
					}
					r.updated_at = r.offer.updated_at;
					return r;
				}),
				[so.field || so.id],
				[so.asc ? "asc" : "desc"]
			);
		},
	},
	mounted() {
		let that = this;
		this.$root.$on("new-active-marker-id", function (newId) {
			console.log("NEW ACTIVE MARKER ID", newId);
			that.activeId = newId;
			that.$nextTick(function () {
				that.scrollToActiveListItem();
			});
		});
	},
	watch: {
		initialFilter: {
			deep: true,
			handler() {
				if (
					JSON.stringify(this.initialFilter) !== JSON.stringify(this.filter)
				) {
					this.applyFilter();
				} else {
					this.resultsCopy = this.results;
				}
			},
		},
	},
};
</script>

<style scoped>
/* https://stackoverflow.com/questions/7492062/css-overflow-scroll-always-show-vertical-scroll-bar */

::-webkit-scrollbar {
	-webkit-appearance: none;
	width: 7px;
}

::-webkit-scrollbar-thumb {
	border-radius: 4px;
	background-color: rgba(0, 0, 0, 0.5);
	box-shadow: 0 0 1px rgba(24, 23, 23, 0.5);
}
</style>
